import React, { useState, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useDineAddonGroups } from 'hooks/api/useDineOps';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineAddonGroup } from 'api/types';
import DineOpsAddonGroupModal from 'components/modals/modules/dineops-module/DineOpsAddonGroupModal';

/**
 * MENU-002 — add-on sets.
 *
 * A set is a named list of extras a menu group offers. Its `menuGroupCount` is
 * the column that matters: a set in use cannot be deleted, because the dishes in
 * those groups would silently stop offering any extras at all.
 */

interface Filters {
  inUse?: string;
  includeInactive?: string;
}

const DineOpsAddonGroupsScreen: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [confirmDelete, setConfirmDelete] = useState<DineAddonGroup | null>(null);
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; record: DineAddonGroup | null } | null>(null);

  const query = useMemo(
    () => ({
      page: 1,
      pageSize: 200,
      searchTerm: searchQuery || undefined,
      includeInactive: filters.includeInactive === 'true' || undefined,
    }),
    [searchQuery, filters.includeInactive],
  );

  const { data: groups, loading, error, refetch } = useDineAddonGroups(query);

  /*
   * "In use" is filtered client side, unlike the search. It is derived from a
   * count the API already returns, so sending it to the server would be a round
   * trip for something already in hand.
   */
  const rows = useMemo(() => {
    if (!filters.inUse) return groups;
    const wantUsed = filters.inUse === 'true';
    return groups.filter((g) => (g.menuGroupCount > 0) === wantUsed);
  }, [groups, filters.inUse]);

  const handleRefresh = useCallback(() => void refetch(), [refetch]);
  const handleAdd = useCallback(() => setEditing({ mode: 'create', record: null }), []);

  const handleDelete = useCallback(async () => {
    if (!confirmDelete) return;
    try {
      await dineOpsService.deleteAddonGroup(confirmDelete.id, confirmDelete.rowVersion);
      notificationService.success('Removed', `'${confirmDelete.addonGroupName}' deleted.`);
      setConfirmDelete(null);
      void refetch();
    } catch (err: any) {
      // The API distinguishes "a menu group still uses this" (409) from a stale
      // row. Passing its words through saves the user guessing which.
      notificationService.error(
        'Not deleted',
        err?.response?.data?.message ?? 'The add-on set could not be deleted.',
      );
      setConfirmDelete(null);
    }
  }, [confirmDelete, refetch]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: handleAdd, tooltip: 'Add an add-on set', variant: 'icon' },
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: handleRefresh, tooltip: 'Refresh', variant: 'icon' },
    ],
    [handleAdd, handleRefresh],
  );

  const columns: SyutGridColumn<DineAddonGroup>[] = useMemo(
    () => [
      { id: 'addonGroupName', header: 'Set', accessorKey: 'addonGroupName', enableSorting: true, size: 220 },
      { id: 'description', header: 'Description', accessorKey: 'description', enableSorting: false, size: 280 },
      {
        id: 'addons',
        header: 'Extras',
        accessorKey: 'addons',
        enableSorting: false,
        size: 300,
        // The names themselves, not a count: "3" tells a manager nothing about
        // whether this is the right set to point a menu group at.
        cell: ({ row }) => {
          const list = row.original.addons ?? [];
          if (list.length === 0) return <span style={{ opacity: 0.6 }}>none yet</span>;
          return (
            <span>
              {list.slice(0, 4).map((a) => `${a.addonName} (${a.price})`).join(', ')}
              {list.length > 4 ? ` +${list.length - 4} more` : ''}
            </span>
          );
        },
      },
      {
        id: 'menuGroupCount',
        header: 'Used by',
        accessorKey: 'menuGroupCount',
        enableSorting: true,
        size: 120,
        cell: ({ row }) =>
          row.original.menuGroupCount === 0
            ? <span style={{ opacity: 0.6 }}>not used</span>
            : `${row.original.menuGroupCount} group(s)`,
      },
      {
        id: 'isActive',
        header: 'Status',
        accessorKey: 'isActive',
        enableSorting: true,
        size: 100,
        cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive'),
      },
    ],
    [],
  );

  const getRowActions = useCallback(
    (row: DineAddonGroup) => [
      { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setEditing({ mode: 'edit', record: row }) },
      {
        id: 'delete',
        label: 'Delete',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setConfirmDelete(row),
        variant: 'danger' as const,
      },
    ],
    [],
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Add-on Groups"
        description="Sets of extras a menu group offers, and the dishes that follow them"
        icon={<IconSystem.tag size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search add-on sets' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={rows}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={
                error
                  ? 'The add-on sets could not be loaded.'
                  : 'No add-on sets match. Clear the search or the filters.'
              }
            />
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}>
                <IconSystem.x size={16} />
              </button>
            </div>

            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="In use"
                  options={[
                    { value: '', label: 'Any' },
                    { value: 'true', label: 'Used by a menu group' },
                    { value: 'false', label: 'Not used' },
                  ]}
                  value={filters.inUse || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, inUse: (v as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>

              <div className="filter-section">
                <SyutSelect
                  label="Include inactive"
                  options={[{ value: '', label: 'Active only' }, { value: 'true', label: 'Include inactive' }]}
                  value={filters.includeInactive || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, includeInactive: (v as string) || undefined }))}
                  placeholder="Active only"
                  size="sm"
                  isClearable
                />
              </div>
            </div>

            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <DineOpsAddonGroupModal
          isOpen
          mode={editing.mode}
          record={editing.record}
          onSave={() => void refetch()}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          open
          title="Delete this add-on set?"
          message={
            confirmDelete.menuGroupCount > 0
              ? `'${confirmDelete.addonGroupName}' is offered by ${confirmDelete.menuGroupCount} menu group(s). `
                + 'It cannot be deleted while they point at it — their dishes would stop offering any extras.'
              : `'${confirmDelete.addonGroupName}' and its extras will be removed.`
          }
          confirmLabel="Delete"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default DineOpsAddonGroupsScreen;
